import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faWrench, faBriefcase, faIndustry, faPeopleRoof,
  faAngleDown, faLock, faUser, faMagnifyingGlassChart,
  faBalanceScale, faFileContract, faHandshake,
  faEnvelope, faFileAlt
} from '@fortawesome/free-solid-svg-icons';
import './Home.css';
import SEO from "../components/SEO";
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';

function Home() {
  const [isExpanded, setIsExpanded] = useState(false);
  const toggleExpand = () => setIsExpanded(!isExpanded);

  const navigate = useNavigate();
  const location = useLocation();

  // 🧭 Navigazioni
  const navigateTo = (path) => navigate(path);
  const navigateToService = (hash) => navigate('/il-nostro-metodo', { state: { hash } });
  const navigateToPolicies = (hash) => navigate('/polizze-assicurative', { state: { hash } });

  useEffect(() => {
    if (location.pathname === "/") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [location.pathname]);

  // 🪄 Varianti animazione fade-in morbida
  const fadeInVariants = {
    hidden: { opacity: 0, y: 25, scale: 0.98 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.9,
        ease: [0.25, 0.1, 0.25, 1], // ease-in-out naturale
        delay: 0.25,
      },
    },
    exit: {
      opacity: 0,
      y: -20,
      transition: { duration: 0.4, ease: 'easeInOut' },
    },
  };

  const methodItems = [
    { id: 1, title: "Consulenza Personalizzata", icon: faUser, hash: "1" },
    { id: 2, title: "Analisi dei Rischi", icon: faMagnifyingGlassChart, hash: "2" },
    { id: 3, title: "Ricerca e Confronto", icon: faBalanceScale, hash: "3" },
    { id: 4, title: "Gestione delle Polizze", icon: faFileContract, hash: "4" },
    { id: 5, title: "Assistenza nei Sinistri", icon: faHandshake, hash: "5" },
  ];

  // 📱 Mobile detector
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const [formData, setFormData] = useState({
    name: "",
    surname: "",
    company: "",
    phone: "",
    email: "",
    request: "",
  });

  const [sending, setSending] = useState(false);
  const [resultMsg, setResultMsg] = useState("");

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    setResultMsg("");

    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ formType: "home", data: formData }),
      });

      const json = await res.json().catch(() => ({}));
      if (!res.ok || !json.ok) throw new Error(json.error || "Invio fallito");

      setResultMsg("Richiesta inviata correttamente. Ti contatteremo a breve.");
      setFormData({
        name: "",
        surname: "",
        company: "",
        phone: "",
        email: "",
        request: "",
      });
    } catch (err) {
      console.error("Errore invio form:", err);
      setResultMsg("Si è verificato un errore durante l'invio. Riprova.");
    } finally {
      setSending(false);
    }
  };

  return (
      <motion.div
        variants={fadeInVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
        transition={{ staggerChildren: 0.15, duration: 0.6 }}
      >
      <div className="home">
      
      <SEO
        title="Andolfatto Insurance Broker"
        description="Andolfatto Insurance Broker offre consulenza assicurativa indipendente per privati, professionisti e aziende. Dal 1974 proteggiamo ciò che conta con soluzioni su misura."
        url="https://andolfattobroker.it/"
      />

        {/* HERO */}
        <section className="hero">
          <div className="hero-content">
            <h1>
              LA TUA SICUREZZA,
              <span className="hero-break"> IL NOSTRO IMPEGNO</span>
            </h1>
            {/* H2 nascosti SEO */}
            <h2 className="visually-hidden">
              Broker assicurativo indipendente a Bassano del Grappa e in tutto il Veneto.
            </h2>

            <h2 className="visually-hidden">
              Consulenza assicurativa personalizzata per privati, professionisti e aziende.
            </h2>

            <h2 className="visually-hidden">
              Soluzioni assicurative su misura per proteggere persone, imprese e patrimoni.
            </h2>

            <h2 className="visually-hidden">
              Andolfatto Insurance Broker — esperienza e innovazione al servizio della sicurezza.
            </h2>
            <p>Dal 1974 proteggiamo persone, professionisti e aziende con soluzioni assicurative su misura.</p>
            <button className="hero-button" onClick={() => navigateTo('/chi-siamo')}>
              Scopri di più
            </button>
          </div>
          <div className="hero-image">
            <motion.img
              src="/assets/images/Immagine Hero 3.png" 
              alt="Broker assicurativo Andolfatto - consulenza e soluzioni assicurative"
              initial={{ opacity: 0, x: 60, scale: 0.95 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
            />
          </div>
        </section>

        {/* IL NOSTRO METODO */}
        <section className="home-method-section">
          <div className="method-header">
            <h2>Il Nostro Metodo</h2>
            <div className="method-divider"></div>
          </div>
          {/* Versione mobile → colonna centrale */}
          {isMobile ? (
            <div className="method-list-mobile">
              {methodItems.map((item) => (
                <div
                  key={item.id}
                  className="method-card-mobile"
                  onClick={() => navigateToService(item.hash)}
                >
                  <motion.div
                    className="method-card-content-mobile"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <div className="method-icon-left-mobile">
                      <FontAwesomeIcon icon={item.icon} />
                    </div>
                    <div className="method-text-mobile">
                      <h3>{item.title}</h3>
                    </div>
                  </motion.div>
                </div>
              ))}
            </div>
          ) : (
          /* Versione desktop → colonna centrale */
          <div className="method-list-desktop">
            {methodItems.map((item) => (
              <div
                key={item.id}
                className="method-card-desktop"
                onClick={() => navigateToService(item.hash)}
              >
                <motion.div
                  className="method-card-content"
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <div className="method-icon-left">
                    <FontAwesomeIcon icon={item.icon} />
                  </div>
                  <div className="method-text">
                    <h3>{item.title}</h3>
                  </div>
                </motion.div>
              </div>
              ))}
            </div>
          )}
        </section>
        
        {/* CATEGORIE */}
        <section className="protection">
          <h1>Polizze Assicurative</h1>
          <div className="protection-divider"></div>
          <div className="categories-container">
            <div className="category" onClick={() => navigateToPolicies('artigiani')}>
              <div className="category-icon">
                <FontAwesomeIcon icon={faWrench} size="lg" />
              </div>
              <h3>Artigiani</h3>
              <p>Proteggi ogni aspetto del tuo lavoro</p>
            </div>
            <div className="category" onClick={() => navigateToPolicies('professionisti')}>
              <div className="category-icon">
                <FontAwesomeIcon icon={faBriefcase} size="lg" />
              </div>
              <h3>Professionisti</h3>
              <p>Copertura assicurativa su misura per ogni professione</p>
            </div>
            <div className="category" onClick={() => navigateToPolicies('industrie')}>
              <div className="category-icon">
                <FontAwesomeIcon icon={faIndustry} size="lg" />
              </div>
              <h3>Industrie</h3>
              <p>Copertura assicurativa completa per il settore industriale</p>
            </div>
            <div className="category" onClick={() => navigateToPolicies('privati')}>
              <div className="category-icon">
                <FontAwesomeIcon icon={faPeopleRoof} size="lg" />
              </div>
              <h3>Privati</h3>
              <p>La protezione che ti segue nella vita di tutti i giorni</p>
            </div>
          </div>
        </section>

        {/* FORM CONTATTI ESPANDIBILE */}
        <section className={`contact-form1 ${isExpanded ? 'expanded' : 'collapsed'}`}>
          <div className="contact-header1" onClick={toggleExpand}>
            <h2>Hai Qualche Domanda?</h2>
            <motion.span
              style={{ display: 'inline-flex' }}
              animate={{ rotate: isExpanded ? 180 : 0 }}
              transition={{ duration: 0.5, ease: "easeInOut" }}
            >
              <FontAwesomeIcon icon={faAngleDown} className="expand-icon" />
            </motion.span>
          </div>
          <motion.div
            className="contact-form-content1"
            initial={false}
            animate={isExpanded ? "open" : "closed"}
            variants={{
              open: { opacity: 1, height: "auto", y: 0, scale: 1 },
              closed: { opacity: 0, height: 0, y: -20, scale: 0.95 }
            }}
            transition={{ duration: 0.7, ease: "easeInOut" }}
            style={{ overflow: "hidden", transformOrigin: "top" }}
          >
            <div className="contact-left1">
              {/* FORM A SINISTRA */}
              <div className="contact-form-text1">
                <p>Non esitare a compilare questo semplice modulo.</p>
              </div>
              <form className="form-container1" onSubmit={handleSubmit}>
                <div className="form-row1">
                  <div className="form-group">
                    <div className="input-wrapper1">
                      <FontAwesomeIcon icon={faUser} className="input-icon1" />
                      <input
                        type="text"
                        id="name"
                        name="name"
                        autoComplete="given-name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder=" "
                        required
                      />
                      <label htmlFor="name">Nome*</label>
                    </div>
                  </div>
                  <div className="form-group">
                    <div className="input-wrapper1">
                      <FontAwesomeIcon icon={faUser} className="input-icon1" />
                      <input
                        type="text"
                        id="surname"
                        name="surname"
                        autoComplete="family-name"
                        value={formData.surname}
                        onChange={handleChange}
                        placeholder=" "
                        required
                      />
                      <label htmlFor="surname">Cognome*</label>
                    </div>
                  </div>
                </div>

                <div className="form-group full-width">
                  <div className="input-wrapper1">
                    <FontAwesomeIcon icon={faEnvelope} className="input-icon1" />
                    <input
                      type="email"
                      id="email"
                      name="email"
                      autoComplete="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder=" "
                      required
                    />
                    <label htmlFor="email">Email*</label>
                  </div>
                </div>

                <div className="form-group full-width">
                  <div className="input-wrapper1">
                    <FontAwesomeIcon icon={faFileAlt} className="input-icon1" />
                    <textarea
                      id="request"
                      name="request"
                      autoComplete="off"
                      value={formData.request}
                      onChange={handleChange}
                      placeholder=" "
                      required
                    />
                    <label htmlFor="request">Richiesta*</label>
                  </div>
                </div>

                <div className="button-container1">
                  <button type="submit" className="contact-button" disabled={sending}>
                    {sending ? 'Invio…' : 'Invia'}
                  </button>
                  <button
                    type="button"
                    className="privacy-button1"
                    onClick={() =>
                      window.open(
                        'https://www.iubenda.com/privacy-policy/86004529',
                        '_blank'
                      )
                    }
                  >
                    <FontAwesomeIcon icon={faLock} className="lock-icon" />
                    Privacy Policy
                  </button>
                </div>

                {resultMsg && (
                  <p className="form-result-message" style={{ marginTop: '10px' }}>
                    {resultMsg}
                  </p>
                )}
              </form>
            </div>

            <div className="contact-right1">
              <div className="preventivo-section">
              <h3>Vuoi richiedere un preventivo?</h3>
              <p>Scegli se sei un'azienda o un privato e compila il form dedicato.</p>
              <div className="preventivo-buttons">
                <button
                  className="preventivo-button azienda"
                  onClick={() => navigate('/contatti?tipo=azienda')}
                >
                  Azienda
                </button>
                <button
                  className="preventivo-button privato"
                  onClick={() => navigate('/contatti?tipo=privato')}
                >
                  Privato
                </button>
              </div>
            </div>
            </div>
          </motion.div>
        </section>


      </div>
    </motion.div>
  );
}

export default Home;
